import { TurnErrorKind } from "./slopifyThread";

export interface SlopifyContextItem {
  label: string;
  snippet: string | null;
}

export interface SlopifyContextLayer {
  key: string;
  depth: number;
  active: boolean;
  includedInPrompt: boolean;
  truncated: boolean;
  snippet?: string | null;
  items?: SlopifyContextItem[] | null;
}

export interface SlopifyGenerateRequest {
  contentId: number;
  humanPrompt?: string;
  targetLanguageId?: number;
  scopeFields?: string[];
  excludeContextLayers?: string[];
  signal?: AbortSignal;
  apiUrl: string;
}

export interface SlopifyGenerateSuccess {
  success: true;
  contentId: number;
  contentType: string;
  generatedData: Record<string, unknown>;
  contextSummary?: SlopifyContextLayer[];
  rateLimitRemaining?: number;
  rateLimitReset?: number;
}

export class SlopifyClientError extends Error {
  kind: TurnErrorKind;
  retryAfter?: number;
  constructor(kind: TurnErrorKind, message?: string, retryAfter?: number) {
    super(message ?? kind);
    this.kind = kind;
    this.retryAfter = retryAfter;
  }
}

const parseNumericHeader = (value: string | null): number | undefined => {
  if (value === null) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

export async function generateSlopify({
  contentId,
  humanPrompt,
  targetLanguageId,
  scopeFields,
  excludeContextLayers,
  signal,
  apiUrl,
}: SlopifyGenerateRequest): Promise<SlopifyGenerateSuccess> {
  const jwt = localStorage.getItem("wixar.auth.jwt");
  if (!jwt) {
    throw new SlopifyClientError("auth", "Missing auth token");
  }

  let response: Response;
  try {
    response = await fetch(apiUrl, {
      method: "POST",
      signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        contentId,
        humanPrompt: humanPrompt && humanPrompt.length > 0 ? humanPrompt : undefined,
        targetLanguageId: typeof targetLanguageId === "number" ? targetLanguageId : undefined,
        scopeFields: Array.isArray(scopeFields) && scopeFields.length > 0 ? scopeFields : undefined,
        excludeContextLayers:
          Array.isArray(excludeContextLayers) && excludeContextLayers.length > 0
            ? excludeContextLayers
            : undefined,
      }),
    });
  } catch (err: any) {
    if (err?.name === "AbortError") {
      throw new SlopifyClientError("aborted");
    }
    throw new SlopifyClientError("network", err?.message);
  }

  const rateLimitRemaining = parseNumericHeader(response.headers.get("X-RateLimit-Remaining"));
  const rateLimitReset = parseNumericHeader(response.headers.get("X-RateLimit-Reset"));

  if (response.status === 429) {
    throw new SlopifyClientError(
      "rate-limit",
      "Rate limit exceeded",
      typeof rateLimitReset === "number" ? rateLimitReset * 1000 : undefined,
    );
  }

  if (!response.ok) {
    let bodyText = "";
    try {
      bodyText = await response.text();
    } catch {
      /* ignore */
    }
    if (response.status === 500 && /did not match expected schema/i.test(bodyText)) {
      throw new SlopifyClientError("schema", "Invalid schema response");
    }
    throw new SlopifyClientError("network", `HTTP ${response.status}: ${bodyText.slice(0, 200)}`);
  }

  let body: any;
  try {
    body = await response.json();
  } catch (err: any) {
    throw new SlopifyClientError("network", "Invalid JSON response");
  }

  if (!body?.success || !body?.generatedData || Object.keys(body.generatedData).length === 0) {
    throw new SlopifyClientError("schema", "Empty or invalid generated data");
  }

  return {
    success: true,
    contentId: body.contentId,
    contentType: body.contentType,
    generatedData: body.generatedData,
    contextSummary: Array.isArray(body.contextSummary) ? body.contextSummary : undefined,
    rateLimitRemaining,
    rateLimitReset,
  };
}
